<!--
  The optional details of a media item: note, primary tag (fixed list), secondary tags (free
  text with suggestions, stored lowercased), body site (fixed list + detail), diagnosis (the
  patient's own, from orders_diagnosis), who took it when that wasn't the uploader, and who can
  see it (staff only by default).
  With `withPatient` it also asks "Associate with a patient?", as the upload step does.
-->
<template>
  <div class="md-tagform">
    <template v-if="withPatient">
      <div class="md-field">
        <span class="md-label">Associate with a patient?</span>
        <div class="md-seg">
          <button type="button" :class="{ on: modelValue.patient_unique_id !== null }" @click="setPatient(lastPatient)">Yes (patient)</button>
          <button type="button" :class="{ on: modelValue.patient_unique_id === null }" @click="setPatient(null)">No (my library)</button>
        </div>
        <span v-if="modelValue.patient_unique_id === null" class="md-small md-muted">
          Stays in your own library: only you see it, and it isn't part of any chart until you assign it to a patient.
        </span>
      </div>
      <div v-if="modelValue.patient_unique_id !== null" class="md-field">
        <label for="md-tf-patient">Patient *</label>
        <select id="md-tf-patient" class="md-select" :value="modelValue.patient_unique_id" @change="setPatient($event.target.value)">
          <option v-for="p in store.patients" :key="p.unique_ID" :value="p.unique_ID">{{ p.name }} (DOB {{ p.date_of_birth }})</option>
        </select>
      </div>
    </template>

    <div class="md-field">
      <label for="md-tf-note">{{ noteLabel }} <span class="md-muted md-small">(optional)</span></label>
      <textarea id="md-tf-note" class="md-textarea" maxlength="500" :value="modelValue.note" placeholder="e.g. Bruising on left leg, improved compared to last week"
        @input="patch({ note: $event.target.value })" />
      <span class="md-small md-muted" style="text-align:right">{{ (modelValue.note || '').length }}/500</span>
    </div>

    <div class="md-tagform-two">
      <div class="md-field">
        <label for="md-tf-primary">Primary tag</label>
        <select id="md-tf-primary" class="md-select" :value="modelValue.primary_tag || ''" @change="patch({ primary_tag: $event.target.value || null })">
          <option value="">None</option>
          <option v-for="t in store.config.primary_tags" :key="t.code" :value="t.code">{{ t.label }}</option>
        </select>
      </div>
      <div class="md-field">
        <label for="md-tf-site">Body site</label>
        <select id="md-tf-site" class="md-select" :value="modelValue.body_site || ''" @change="patch({ body_site: $event.target.value || null })">
          <option value="">None</option>
          <option v-for="t in store.config.body_sites" :key="t.code" :value="t.code">{{ t.label }}</option>
        </select>
      </div>
    </div>
    <div v-if="modelValue.body_site" class="md-field">
      <label for="md-tf-site-detail">Body site detail <span class="md-muted md-small">(optional)</span></label>
      <input id="md-tf-site-detail" class="md-input" maxlength="120" :value="modelValue.body_site_detail" placeholder="e.g. lateral shin" @input="patch({ body_site_detail: $event.target.value })" />
    </div>

    <div class="md-field">
      <label for="md-tf-tag">Secondary tags</label>
      <div class="md-tagform-tags">
        <span v-for="t in modelValue.tags" :key="t" class="md-tag">{{ t }} <button type="button" class="md-x" :aria-label="`Remove ${t}`" @click="removeTag(t)">×</button></span>
        <input id="md-tf-tag" v-model="draftTag" class="md-input md-tagform-taginput" list="md-tf-suggest" placeholder="Add tag (bruise, left leg…)" @keydown="onTagKey" />
        <button type="button" class="md-btn ghost sm" :disabled="!draftTag.trim()" @click="addTag">+ Add tag</button>
      </div>
      <datalist id="md-tf-suggest">
        <option v-for="s in suggestions" :key="s" :value="s" />
      </datalist>
    </div>

    <div v-if="modelValue.patient_unique_id !== null" class="md-field">
      <label for="md-tf-dx">Diagnosis <span class="md-muted md-small">(optional, from this patient's diagnoses)</span></label>
      <select id="md-tf-dx" class="md-select" :value="modelValue.orddiagid || ''" @change="patch({ orddiagid: $event.target.value ? Number($event.target.value) : null })">
        <option value="">None</option>
        <option v-for="d in diagnoses" :key="d.orddiagid" :value="d.orddiagid">{{ d.diagnosis_code }} · {{ d.label }}</option>
      </select>
    </div>

    <div class="md-field">
      <label for="md-tf-takenby">Taken by <span class="md-muted md-small">(if not the uploader)</span></label>
      <select id="md-tf-takenby" class="md-select" :value="modelValue.captured_by_unique_id || ''" @change="patch({ captured_by_unique_id: $event.target.value || null })">
        <option value="">The uploader</option>
        <option v-for="(n, uid) in store.config.people" :key="uid" :value="uid">{{ n }}</option>
      </select>
    </div>

    <label v-if="modelValue.patient_unique_id !== null" class="md-check">
      <input type="checkbox" :checked="!!modelValue.patient_visible" @change="patch({ patient_visible: $event.target.checked ? 1 : 0 })" />
      <span><strong>The patient can see this</strong><br><span class="md-small md-muted">Off by default: staff only. Family contacts don't see patient media.</span></span>
    </label>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useMediaStore } from '@/stores/mediaStore';
import { listPatientDiagnoses, listTagSuggestions } from '@/api/mediaApi';

const props = defineProps({
  modelValue: { type: Object, required: true },
  withPatient: { type: Boolean, default: false },
  // "Note" is the item's own text; next to a clinical note it reads as a caption instead.
  noteLabel: { type: String, default: 'Note' },
});
const emit = defineEmits(['update:modelValue']);
const store = useMediaStore();
const draftTag = ref('');
const diagnoses = ref([]);
const suggestions = ref([]);
const lastPatient = store.lastPatientUid || (store.patients[0] || {}).unique_ID || null;

function patch(fields) { emit('update:modelValue', { ...props.modelValue, ...fields }); }
function setPatient(uid) { patch({ patient_unique_id: uid, orddiagid: null, patient_visible: uid ? props.modelValue.patient_visible : 0 }); }
function addTag() {
  const t = draftTag.value.trim().toLowerCase().replace(/,$/, '');
  if (t && !props.modelValue.tags.includes(t)) patch({ tags: [...props.modelValue.tags, t] });
  draftTag.value = '';
}
function onTagKey(e) { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }
function removeTag(t) { patch({ tags: props.modelValue.tags.filter((x) => x !== t) }); }

watch(() => props.modelValue.patient_unique_id, async (uid) => {
  diagnoses.value = uid ? await listPatientDiagnoses(uid) : [];
}, { immediate: true });
listTagSuggestions().then((s) => { suggestions.value = s; });
</script>

<style scoped>
.md-tagform-two { display:grid; grid-template-columns:1fr 1fr; gap:0 12px; }
.md-tagform-tags { display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.md-tagform-taginput { flex:1 1 180px; width:auto; }
.md-x { border:none; background:none; color:inherit; cursor:pointer; padding:0 0 0 2px; font-size:14px; }
.md-check { display:flex; gap:10px; align-items:flex-start; cursor:pointer; padding:10px 12px; border:1px solid var(--line); border-radius:8px; }
.md-check input { margin-top:4px; width:18px; height:18px; }
@media (max-width: 480px) { .md-tagform-two { grid-template-columns:1fr; } }
</style>
